/**
 * Key Management System dla E2EE w aplikacji 2domy
 * Zarządza kluczami szyfrowania dla użytkowników i rodzin
 */

import { encryptData, decryptData, type EncryptedData } from './encryption';
import { getDB } from './indexedDb';
import { supabase } from '../lib/supabase';

// Interfejs dla klucza rodziny
export interface FamilyKey {
  id: string;
  familyId: string;
  encryptedKey: string; // Klucz rodziny zaszyfrowany kluczem użytkownika
  version: number;
  createdAt: string;
}

// Interfejs dla klucza użytkownika
export interface UserKey {
  id: string;
  userId: string;
  salt: string; // Sól używana do derywacji klucza z hasła
  verifier: string; // Zaszyfrowany znany tekst do weryfikacji poprawności klucza
  createdAt: string;
}

// Stałe dla key managementu
const KEY_VERIFICATION_TEXT = 'VALID_2DOMY_KEY';
const KEY_VERSION = 1;

/**
 * Generuje nowy klucz użytkownika na podstawie hasła
 * @param userId ID użytkownika
 * @param password Hasło użytkownika
 */
export const generateUserKey = async (userId: string, password: string): Promise<UserKey> => {
  // Szyfrujemy tekst weryfikacyjny kluczem derywowanym z hasła
  const encryptedVerifier = await encryptData(KEY_VERIFICATION_TEXT, password);
  
  const userKey: UserKey = {
    id: `user-key-${Date.now()}`,
    userId,
    salt: encryptedVerifier.salt,
    verifier: JSON.stringify(encryptedVerifier),
    createdAt: new Date().toISOString()
  };
  
  // Zapisz klucz lokalnie
  const db = await getDB();
  await db.put('encryption_keys', userKey);
  
  return userKey;
};

/**
 * Weryfikuje, czy hasło użytkownika jest poprawne
 * @param userId ID użytkownika
 * @param password Hasło do weryfikacji
 */
export const verifyUserPassword = async (userId: string, password: string): Promise<boolean> => {
  try {
    // Pobierz klucz użytkownika
    const db = await getDB();
    const userKey = await db.get('encryption_keys', userId);
    
    if (!userKey) {
      return false;
    }
    
    // Próbuj odszyfrować weryfikator
    if (!userKey.verifier) {
      return false;
    }
    const encryptedVerifier = JSON.parse(userKey.verifier) as EncryptedData;
    const decrypted = await decryptData(encryptedVerifier, password);
    
    return decrypted === KEY_VERIFICATION_TEXT;
  } catch (error) {
    console.error('❌ Błąd weryfikacji hasła:', error);
    return false;
  }
};

/**
 * Generuje nowy klucz rodziny i szyfruje go kluczem użytkownika
 * @param familyId ID rodziny
 * @param userId ID użytkownika
 * @param password Hasło użytkownika
 */
export const generateFamilyKey = async (
  familyId: string, 
  userId: string, 
  password: string
): Promise<FamilyKey> => {
  // Generuj losowy klucz rodziny (32 bajty = 256 bitów)
  const familyKeyBytes = window.crypto.getRandomValues(new Uint8Array(32));
  const familyKeyBase64 = btoa(String.fromCharCode(...familyKeyBytes));
  
  // Szyfruj klucz rodziny hasłem użytkownika
  const encryptedFamilyKey = await encryptData(familyKeyBase64, password);
  
  const familyKey: FamilyKey = {
    id: `family-key-${Date.now()}`,
    familyId,
    encryptedKey: JSON.stringify(encryptedFamilyKey),
    version: KEY_VERSION,
    createdAt: new Date().toISOString()
  };
  
  // Zapisz klucz lokalnie
  const db = await getDB();
  await db.put('encryption_keys', familyKey);
  
  // Zapisz klucz w Supabase (w tabeli family_keys)
  try {
    await supabase.from('family_keys').insert({
      family_id: familyId,
      user_id: userId,
      encrypted_key: JSON.stringify(encryptedFamilyKey),
      version: KEY_VERSION,
      created_at: familyKey.createdAt
    });
  } catch (error) {
    console.error('❌ Błąd zapisywania klucza rodziny w Supabase:', error);
    // Kontynuuj, ponieważ klucz jest już zapisany lokalnie
  }
  
  return familyKey;
};

/**
 * Pobiera i deszyfruje klucz rodziny
 * @param familyId ID rodziny
 * @param password Hasło użytkownika
 */
export const getFamilyKey = async (familyId: string, password: string): Promise<string | null> => {
  try {
    // Najpierw próbuj pobrać z lokalnej bazy
    const db = await getDB();
    let familyKey: FamilyKey | undefined;
    
    try {
      // Najpierw próbujemy pobrać klucz za pomocą indeksu
      const familyKeyQuery = await db.getAllFromIndex('encryption_keys', 'by-family', familyId);
      familyKey = familyKeyQuery[0] as FamilyKey | undefined;
    } catch (indexError) {
      // Debug only in non-production
      try {
        // Fallback 1: pobierz wszystkie klucze i filtruj ręcznie
        const allKeys = await db.getAll('encryption_keys');
        familyKey = allKeys.find(key => key.familyId === familyId) as FamilyKey | undefined;
      } catch (getAllError) {
        console.error('❌ Błąd pobierania wszystkich kluczy:', getAllError);
        // Kontynuujemy - spróbujemy pobrać z Supabase
      }
    }
    
    // Jeśli nie ma lokalnie, pobierz z Supabase
    if (!familyKey) {
      const { data, error } = await supabase
        .from('family_keys')
        .select('*')
        .eq('family_id', familyId)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (error || !data || data.length === 0) {
        console.error('❌ Błąd pobierania klucza rodziny z Supabase:', error);
        return null;
      }
      
      // Konwertuj do lokalnego formatu
      familyKey = {
        id: `family-key-${Date.now()}`,
        familyId: data[0].family_id,
        encryptedKey: data[0].encrypted_key,
        version: data[0].version,
        createdAt: data[0].created_at
      };
      
      // Zapisz lokalnie dla przyszłego użycia
      await db.put('encryption_keys', familyKey);
    }
    
    // Deszyfruj klucz rodziny
    if (!familyKey.encryptedKey) {
      return null;
    }
    const encryptedKey = JSON.parse(familyKey.encryptedKey) as EncryptedData;
    return await decryptData(encryptedKey, password);
    
  } catch (error) {
    console.error('❌ Błąd pobierania klucza rodziny:', error);
    return null;
  }
};

/**
 * Szyfruje dane kluczem rodziny
 * @param data Dane do zaszyfrowania
 * @param familyId ID rodziny
 * @param password Hasło użytkownika
 */
export const encryptWithFamilyKey = async (
  data: string,
  familyId: string,
  password: string
): Promise<EncryptedData | null> => {
  try {
    // Pobierz klucz rodziny
    const familyKey = await getFamilyKey(familyId, password);
    if (!familyKey) {
      // Jeśli klucz rodziny nie istnieje, stwórz nowy
      // Debug only in non-production
      try {
        await generateFamilyKey(familyId, 'unknown-user', password);
        const newFamilyKey = await getFamilyKey(familyId, password);
        if (!newFamilyKey) {
          throw new Error('Nie udało się utworzyć klucza rodziny');
        }
        return await encryptData(data, newFamilyKey);
      } catch (genError) {
        console.error('❌ Błąd generowania klucza rodziny:', genError);
        throw new Error('Nie można utworzyć klucza rodziny');
      }
    }
    
    // Szyfruj dane kluczem rodziny
    return await encryptData(data, familyKey);
  } catch (error) {
    console.error('❌ Błąd szyfrowania danych kluczem rodziny:', error);
    return null;
  }
};

/**
 * Deszyfruje dane kluczem rodziny
 * @param encryptedData Zaszyfrowane dane
 * @param familyId ID rodziny
 * @param password Hasło użytkownika
 */
export const decryptWithFamilyKey = async (
  encryptedData: EncryptedData,
  familyId: string,
  password: string
): Promise<string | null> => {
  try {
    // Pobierz klucz rodziny
    const familyKey = await getFamilyKey(familyId, password);
    if (!familyKey) {
      throw new Error('Nie można pobrać klucza rodziny');
    }
    
    // Deszyfruj dane kluczem rodziny
    if (familyKey === null) {
      throw new Error('Klucz rodziny jest null');
    }
    return await decryptData(encryptedData, familyKey);
  } catch (error) {
    console.error('❌ Błąd deszyfrowania danych kluczem rodziny:', error);
    return null;
  }
};

/**
 * Udostępnia klucz rodziny innemu użytkownikowi
 * @param familyId ID rodziny
 * @param targetUserId ID użytkownika, któremu udostępniamy klucz
 * @param password Hasło użytkownika udostępniającego
 */
export const shareFamilyKey = async (
  familyId: string,
  targetUserId: string,
  password: string
): Promise<boolean> => {
  try {
    // Pobierz klucz rodziny
    const familyKey = await getFamilyKey(familyId, password);
    if (!familyKey) {
      throw new Error('Nie można pobrać klucza rodziny');
    }
    
    // Szyfruj klucz rodziny hasłem użytkownika (tymczasowo używamy tego samego hasła)
    // W pełnej implementacji należałoby użyć klucza publicznego odbiorcy
    if (familyKey === null) {
      throw new Error('Klucz rodziny jest null');
    }
    const encryptedFamilyKey = await encryptData(familyKey, password);
    
    // Zapisz zaszyfrowany klucz dla odbiorcy
    const { error } = await supabase.from('family_keys').insert({
      family_id: familyId,
      user_id: targetUserId,
      encrypted_key: JSON.stringify(encryptedFamilyKey),
      version: KEY_VERSION,
      created_at: new Date().toISOString()
    });
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('❌ Błąd udostępniania klucza rodziny:', error);
    return false;
  }
};

/**
 * Inicjalizuje system kluczy dla nowego użytkownika
 * @param userId ID użytkownika
 * @param password Hasło użytkownika
 * @param familyId Opcjonalne ID rodziny, jeśli użytkownik od razu dołącza do rodziny
 */
export const initializeUserKeys = async (
  userId: string,
  password: string,
  familyId?: string
): Promise<boolean> => {
  try {
    // Generuj klucz użytkownika
    await generateUserKey(userId, password);
    
    // Jeśli podano ID rodziny, generuj również klucz rodziny
    if (familyId) {
      await generateFamilyKey(familyId, userId, password);
    }
    
    return true;
  } catch (error) {
    console.error('❌ Błąd inicjalizacji kluczy użytkownika:', error);
    return false;
  }
};